'use client'

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/shadcn/collapsible'
import type { ToolCallMessagePartProps } from '@assistant-ui/react'
import { ChevronDown, CircleX, LoaderCircle } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import type { DmpChatToolDisplayMode } from '../dmpChatToolDisplay'
import { IconV2AlertOutline } from '@icons/IconV2AlertOutline'
import { IconV2CorrectOutline } from '@icons/IconV2CorrectOutline'

const DMP_CHAT_MAX_VISIBLE_TOOL_CHARS = 12_000
const DMP_CHAT_TOOL_TRUNCATION_NOTICE = '\n\n[Tool payload truncated for display.]'

type DmpChatToolStatus = ToolCallMessagePartProps['status']

type DmpChatToolStatusPresentation = {
  icon: ReactNode
  label: string
  announcement: string
}

export function DmpChatToolGroup({
  children,
  count,
  status,
}: {
  children: ReactNode
  count: number
  status: DmpChatToolStatus
}) {
  const [userOpen, setUserOpen] = useState<boolean | null>(null)
  const statusPresentation = getToolStatusPresentation(status)
  const isOpen = userOpen ?? status.type !== 'complete'
  const callLabel = count === 1 ? '1 tool call' : `${count} tool calls`

  return (
    <Collapsible
      className="mb-3 w-full rounded-lg border border-border/50 bg-muted/30 px-3 py-2"
      onOpenChange={setUserOpen}
      open={isOpen}
    >
      <CollapsibleTrigger asChild>
        <button
          className="group flex w-full items-center justify-between gap-3 text-left text-sm text-muted-foreground transition-colors hover:text-foreground"
          type="button"
        >
          <span className="flex min-w-0 items-center gap-2">
            {statusPresentation.icon}
            <span className="truncate">
              Tool activity · {callLabel} · {statusPresentation.label}
            </span>
          </span>
          <ChevronDown
            aria-hidden="true"
            className="size-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180"
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent
        aria-busy={status.type === 'running'}
        className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down"
      >
        <div className="space-y-2 pt-3">{children}</div>
      </CollapsibleContent>
      <span aria-atomic="true" aria-live="polite" className="sr-only" role="status">
        {statusPresentation.announcement}
      </span>
    </Collapsible>
  )
}

export function DmpChatToolCall({
  argsText,
  mode,
  result,
  status,
  toolName,
}: {
  argsText: string
  mode: DmpChatToolDisplayMode
  result?: unknown
  status: DmpChatToolStatus
  toolName: string
}) {
  const hasResult = result !== undefined && result !== null
  const displayStatus = getToolDisplayStatus(status, mode, hasResult)
  const statusPresentation = getToolStatusPresentation(displayStatus)
  const argumentDisplay = getArgumentDisplay(argsText, displayStatus)
  const resultDisplay = hasResult
    ? truncateToolText(formatToolValue(result))
    : displayStatus.type === 'running'
      ? 'Waiting for a terminal result.'
      : 'A terminal result was not received.'

  return (
    <div className="min-w-0 rounded-md border border-border/50 bg-background/70 p-3">
      <div className="flex min-w-0 items-center gap-2 text-sm">
        {statusPresentation.icon}
        <span className="min-w-0 flex-1 truncate font-mono font-medium text-foreground">
          {toolName || 'Tool unavailable'}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">{statusPresentation.label}</span>
      </div>
      {mode !== 'name-only' && (
        <ToolPayload label="Arguments" value={argumentDisplay} fallback={argumentDisplay === null} />
      )}
      {mode === 'full-details' && <ToolPayload label="Result" value={resultDisplay} fallback={!hasResult} />}
    </div>
  )
}

function ToolPayload({ fallback, label, value }: { fallback: boolean; label: string; value: string | null }) {
  return (
    <div className="mt-3 min-w-0">
      <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</h4>
      {fallback ? (
        <p className="text-xs text-muted-foreground">{value ?? `${label} unavailable.`}</p>
      ) : (
        <pre className="max-h-48 min-w-0 overflow-auto whitespace-pre-wrap break-words rounded border border-border/50 bg-muted/30 p-2 font-mono text-xs">
          {value}
        </pre>
      )}
    </div>
  )
}

function getArgumentDisplay(argsText: string, status: DmpChatToolStatus): string | null {
  if (!argsText.trim()) {
    return status.type === 'running' ? 'Arguments are still streaming.' : null
  }

  try {
    const parsed: unknown = JSON.parse(argsText)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null
    }

    return truncateToolText(JSON.stringify(parsed, null, 2))
  } catch {
    return null
  }
}

function formatToolValue(value: unknown): string {
  if (typeof value === 'string') {
    return value
  }

  try {
    const serialized = JSON.stringify(value, null, 2)
    return typeof serialized === 'string' ? serialized : 'Value unavailable.'
  } catch {
    return 'Value unavailable.'
  }
}

function truncateToolText(value: string): string {
  if (value.length <= DMP_CHAT_MAX_VISIBLE_TOOL_CHARS) {
    return value
  }

  const visibleLength = DMP_CHAT_MAX_VISIBLE_TOOL_CHARS - DMP_CHAT_TOOL_TRUNCATION_NOTICE.length
  return `${value.slice(0, Math.max(0, visibleLength))}${DMP_CHAT_TOOL_TRUNCATION_NOTICE}`
}

function getToolDisplayStatus(
  status: DmpChatToolStatus,
  mode: DmpChatToolDisplayMode,
  hasResult: boolean
): DmpChatToolStatus {
  return status.type === 'complete' && mode === 'full-details' && !hasResult
    ? { type: 'incomplete', reason: 'other' }
    : status
}

function getToolStatusPresentation(status: DmpChatToolStatus): DmpChatToolStatusPresentation {
  switch (status.type) {
    case 'running':
      return {
        icon: <LoaderCircle aria-hidden="true" className="size-4 shrink-0 animate-spin text-muted-foreground" />,
        label: 'Running',
        announcement: 'Tool activity is running.',
      }
    case 'complete':
      return {
        icon: <IconV2CorrectOutline aria-hidden="true" className="size-4 shrink-0 text-success" />,
        label: 'Completed',
        announcement: 'Tool activity is complete.',
      }
    case 'requires-action':
      return {
        icon: <IconV2AlertOutline aria-hidden="true" className="size-4 shrink-0 text-warning" />,
        label: 'Waiting for input',
        announcement: 'Tool activity is waiting for input.',
      }
    case 'incomplete':
      return {
        icon: <CircleX aria-hidden="true" className="size-4 shrink-0 text-destructive" />,
        label: 'Incomplete',
        announcement: 'Tool activity is incomplete. A terminal result was not confirmed.',
      }
  }
}
